import type { ToolDefinition } from '../../types';
import { Tool } from '../Tool';

import { ApiService, TaskResultMessageTypeEnum } from 'js/src/client';
import WebSocketClient from 'js/src/libs/websocket-client';
import { isErrorObject } from 'js/src/types';

interface SketchResearchArgs {
    research_topic: string;
}

interface SketchResearchResponse {
    research_content: string;
    research_areas: Array<Record<string, unknown>>;
    usage?: Record<string, unknown>;
    processing_time_seconds?: number;
}

export class SketchResearchTool extends Tool<SketchResearchArgs> {
    public static readonly toolName = 'research_topic';

    public override canRunAsync = true;
    public override getToolDefinition(): ToolDefinition {
        return {
            type: 'function',
            function: {
                name: SketchResearchTool.toolName,
                description:
                    'Research a topic using web search and gather relevant information to help create video content',
                parameters: {
                    type: 'object',
                    properties: {
                        research_topic: {
                            type: 'string',
                            description: 'The topic to research for video content creation',
                        },
                    },
                    required: ['research_topic'],
                },
            },
        };
    }

    public override getToolDisplayName(): string {
        return 'Searching the web';
    }

    public get statusIcon(): string {
        return 'travel_explore';
    }

    public async execute(args: SketchResearchArgs) {
        const { research_topic } = args;

        try {
            const response = await ApiService.sketchResearchCreate({
                body: {
                    research_topic: research_topic,
                },
            });

            const ws = WebSocketClient.getInstance();
            const result = await ws.waitForMessage<SketchResearchResponse>(
                response.data.task_id,
                TaskResultMessageTypeEnum.TASK_RESULT,
                'sketch_research',
            );

            if (!result.research_content) {
                return `No research content could be gathered for topic: "${research_topic}".`;
            }

            return `${result.research_content}\n\n`;
        } catch (error) {
            const errorMessage = isErrorObject(error)
                ? error.error.message
                : error instanceof Error
                  ? error.message
                  : 'Unknown error occurred';
            return `Error researching topic: ${errorMessage}`;
        }
    }
}
